import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import type { LocalDate } from "@js-joda/core";
import {
  buildSummary,
  fetchDepositTransactionPage,
  fetchWithdrawalTransactionPage,
  filterTransactionsBySummaryFilter,
  mergeTransactionPages,
  sortTransactionsDescending,
} from "./tips.transactions.logic";
import {
  formatMonthTitle,
  isInMonth,
  toMonthLocalDate,
} from "../logic/tips.format.logic";
import { useTipsTransactionsInfiniteScroll } from "./tips.transactionsInfiniteScroll.hook";
import type {
  TransactionItem,
  TransactionPageResult,
  TransactionSummaryFilter,
  TransactionSummaryModel,
} from "./tips.transactions.types";

interface LoadInitialTransactionsParams {
  readonly requestId: number;
  readonly selectedCompanyId: number;
}

interface UseTipsTransactionsParams {
  readonly companyId: number | null;
  readonly currentDate: LocalDate;
  readonly locale: string;
  readonly onCurrentDateChange: (date: LocalDate) => void;
}

interface UseTipsTransactionsResult {
  readonly activeSummaryFilter: TransactionSummaryFilter;
  readonly hasLoadError: boolean;
  readonly hasMoreTransactions: boolean;
  readonly isLoading: boolean;
  readonly isLoadingMore: boolean;
  readonly loadMoreTriggerRef: RefObject<HTMLDivElement>;
  readonly monthTitle: string;
  readonly summary: TransactionSummaryModel;
  readonly visibleTransactions: readonly TransactionItem[];
  readonly onSummaryFilterChange: (filter: TransactionSummaryFilter) => void;
}

export const useTipsTransactions = (
  params: UseTipsTransactionsParams,
): UseTipsTransactionsResult => {
  const { companyId, currentDate, locale, onCurrentDateChange } = params;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [hasMoreDeposits, setHasMoreDeposits] = useState(false);
  const [hasMoreWithdrawals, setHasMoreWithdrawals] = useState(false);
  const [nextDepositPage, setNextDepositPage] = useState(0);
  const [nextWithdrawalPage, setNextWithdrawalPage] = useState(0);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [activeSummaryFilter, setActiveSummaryFilter] =
    useState<TransactionSummaryFilter>("all");
  const hasAutoSelectedMonthRef = useRef(false);
  const isLoadingPageRef = useRef(false);
  const requestIdRef = useRef(0);
  const shouldSkipNextAutoLoadRef = useRef(false);
  const hasMoreTransactions = hasMoreDeposits || hasMoreWithdrawals;

  const isCurrentRequest = useCallback((requestId: number): boolean => {
    return requestIdRef.current === requestId;
  }, []);

  const applyPageResult = useCallback(
    (pageResult: TransactionPageResult): void => {
      if (pageResult.source === "deposit") {
        setHasMoreDeposits(pageResult.hasMore);
        return;
      }

      setHasMoreWithdrawals(pageResult.hasMore);
    },
    [],
  );

  const loadInitialTransactions = useCallback(
    async ({
      requestId,
      selectedCompanyId,
    }: LoadInitialTransactionsParams): Promise<void> => {
      isLoadingPageRef.current = true;
      setHasLoadError(false);
      setIsLoading(true);
      setIsLoadingMore(true);

      try {
        const pageResults = await Promise.all([
          fetchDepositTransactionPage({
            companyId: selectedCompanyId,
            page: 0,
          }),
          fetchWithdrawalTransactionPage({
            companyId: selectedCompanyId,
            page: 0,
          }),
        ]);

        if (!isCurrentRequest(requestId)) return;

        pageResults.forEach(applyPageResult);
        setNextDepositPage(1);
        setNextWithdrawalPage(1);
        setTransactions(
          pageResults.flatMap((pageResult) => pageResult.transactions),
        );
      } catch {
        if (!isCurrentRequest(requestId)) return;

        setHasLoadError(true);
        setTransactions([]);
        setHasMoreDeposits(false);
        setHasMoreWithdrawals(false);
      } finally {
        if (!isCurrentRequest(requestId)) return;

        isLoadingPageRef.current = false;
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [applyPageResult, isCurrentRequest],
  );

  const loadMoreTransactions = useCallback(async (): Promise<void> => {
    if (!hasMoreTransactions || isLoadingPageRef.current) return;
    if (companyId === null) return;

    const requestId = requestIdRef.current;
    isLoadingPageRef.current = true;
    setHasLoadError(false);
    setIsLoadingMore(true);

    try {
      const pageResults = await Promise.all([
        ...(hasMoreDeposits
          ? [
              fetchDepositTransactionPage({
                companyId,
                page: nextDepositPage,
              }),
            ]
          : []),
        ...(hasMoreWithdrawals
          ? [
              fetchWithdrawalTransactionPage({
                companyId,
                page: nextWithdrawalPage,
              }),
            ]
          : []),
      ]);

      if (!isCurrentRequest(requestId)) return;

      pageResults.forEach((pageResult) => {
        applyPageResult(pageResult);
        if (pageResult.source === "deposit") {
          setNextDepositPage((currentPage) => currentPage + 1);
        } else {
          setNextWithdrawalPage((currentPage) => currentPage + 1);
        }
      });

      setTransactions((currentTransactions) =>
        mergeTransactionPages({
          currentTransactions,
          nextTransactions: pageResults.flatMap(
            (pageResult) => pageResult.transactions,
          ),
        }),
      );
    } catch {
      if (!isCurrentRequest(requestId)) return;

      setHasLoadError(true);
      setHasMoreDeposits(false);
      setHasMoreWithdrawals(false);
    } finally {
      if (!isCurrentRequest(requestId)) return;

      isLoadingPageRef.current = false;
      setIsLoadingMore(false);
    }
  }, [
    applyPageResult,
    companyId,
    hasMoreDeposits,
    hasMoreTransactions,
    hasMoreWithdrawals,
    isCurrentRequest,
    nextDepositPage,
    nextWithdrawalPage,
  ]);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      isLoadingPageRef.current = false;
    };
  }, []);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    hasAutoSelectedMonthRef.current = false;
    shouldSkipNextAutoLoadRef.current = false;
    isLoadingPageRef.current = false;
    setHasLoadError(false);
    setTransactions([]);
    setNextDepositPage(0);
    setNextWithdrawalPage(0);
    setHasMoreDeposits(false);
    setHasMoreWithdrawals(false);

    if (companyId === null) {
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    void loadInitialTransactions({
      requestId,
      selectedCompanyId: companyId,
    });
  }, [companyId, loadInitialTransactions]);

  const monthTransactions = useMemo(
    () =>
      sortTransactionsDescending({
        transactions: transactions.filter((transaction) =>
          isInMonth({
            dateValue: transaction.createdAt,
            monthDate: currentDate,
          }),
        ),
      }),
    [currentDate, transactions],
  );
  const summary = useMemo(
    () => buildSummary({ transactions: monthTransactions }),
    [monthTransactions],
  );
  const visibleTransactions = useMemo(
    () =>
      filterTransactionsBySummaryFilter({
        filter: activeSummaryFilter,
        transactions: monthTransactions,
      }),
    [activeSummaryFilter, monthTransactions],
  );
  const monthTitle = formatMonthTitle({
    date: currentDate,
    locale,
  });

  useEffect(() => {
    if (hasAutoSelectedMonthRef.current) return;
    if (transactions.length === 0 || monthTransactions.length > 0) return;

    const latestTransaction = sortTransactionsDescending({ transactions })[0];

    if (!latestTransaction) return;

    const latestMonth = toMonthLocalDate({
      dateValue: latestTransaction.createdAt,
    });
    if (
      latestMonth.year() === currentDate.year() &&
      latestMonth.monthValue() === currentDate.monthValue()
    ) {
      return;
    }

    shouldSkipNextAutoLoadRef.current = true;
    onCurrentDateChange(latestMonth);
    hasAutoSelectedMonthRef.current = true;
  }, [
    currentDate,
    monthTransactions.length,
    onCurrentDateChange,
    transactions,
  ]);

  useEffect(() => {
    if (shouldSkipNextAutoLoadRef.current) {
      shouldSkipNextAutoLoadRef.current = false;
      return;
    }

    if (monthTransactions.length > 0 || !hasMoreTransactions || isLoadingMore) {
      return;
    }

    void loadMoreTransactions();
  }, [
    hasMoreTransactions,
    isLoadingMore,
    loadMoreTransactions,
    monthTransactions.length,
  ]);

  const loadMoreTriggerRef = useTipsTransactionsInfiniteScroll({
    hasMoreTransactions,
    itemCount: monthTransactions.length,
    onLoadMore: loadMoreTransactions,
  });

  return {
    activeSummaryFilter,
    hasLoadError,
    hasMoreTransactions,
    isLoading,
    isLoadingMore,
    loadMoreTriggerRef,
    monthTitle,
    onSummaryFilterChange: setActiveSummaryFilter,
    summary,
    visibleTransactions,
  };
};
